import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { TabsContent } from "@/components/ui/tabs";
import { Download } from "lucide-react";
import statisticsAPIService, {
	type HocBaScoreStats,
	type SubjectCombinationStats,
	type SubjectScoreStats,
} from "@/lib/statistics-api.service";
import { HocBaHistogramChart } from "./hoc-ba-histogram-chart";
import { HocBaMajorChart } from "./hoc-ba-major-chart";
import { SubjectCombinationMatrix } from "./subject-combination-matrix";
import { SubjectPopularityChart } from "./subject-popularity-chart";
import { SubjectYearTrendChart } from "./subject-year-trend-chart";
import { StatisticsFilterControls, type StatisticsFilters } from "./StatisticsControls";
import { downloadChartPng, toAPIFilter } from "./utils";

interface HocBaTabProps {
	filters?: StatisticsFilters;
	onFiltersChange?: (filters: StatisticsFilters) => void;
	/** Tăng lên mỗi lần trang yêu cầu tải lại; đổi giá trị là fetch lại. */
	refreshToken?: number;
}

/**
 * Tab "Học bạ": điểm học bạ tổ hợp (thang 30) và ma trận tổ hợp × ngành.
 *
 * ĐẶT TÊN — xem `HocBaScoreStats`/`SubjectCombinationStats` trong
 * `statistics-api.service.ts`: đây KHÔNG PHẢI điểm đầu vào/trúng tuyển/xét
 * tuyển (chỉ là phần học bạ, 20% công thức thật), và tổ hợp là tổ hợp hệ
 * thống tự chọn để tổng điểm lớn nhất, KHÔNG PHẢI tổ hợp thí sinh đăng ký.
 *
 * Ba endpoint độc lập nên fetch song song bằng `Promise.all` — một cái lỗi
 * không nên chặn hai cái còn lại hiện dữ liệu.
 */
export default function HocBaTab({
	filters = {},
	onFiltersChange,
	refreshToken = 0,
}: HocBaTabProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	// `undefined` = lần tải đầu chưa xong; refetch giữ dữ liệu cũ, không rơi về
	// skeleton mỗi lần đổi bộ lọc (CLAUDE.md §1).
	const [scoreStats, setScoreStats] = useState<HocBaScoreStats | undefined>(undefined);
	const [combinationStats, setCombinationStats] = useState<SubjectCombinationStats | undefined>(
		undefined,
	);
	const [subjectScoreStats, setSubjectScoreStats] = useState<SubjectScoreStats | undefined>(
		undefined,
	);
	const [error, setError] = useState<string | null>(null);

	// Tab này KHÔNG áp phạm vi "Ngày tiếp nhận" của trang: bộ lọc đó đã gỡ khỏi
	// giao diện ở đây (điểm học bạ là dữ liệu đầu vào của thí sinh, không phải
	// một dòng sự kiện theo ngày), nên vẫn gửi lên server thì nó lọc trong im
	// lặng mà không có control nào giải thích.
	const apiFilter = useMemo(() => {
		const { receptionDateRange: _ignored, ...rest } = filters;
		return toAPIFilter(rest, "total");
	}, [filters]);

	useEffect(() => {
		let cancelled = false;

		Promise.all([
			statisticsAPIService.getHocBaScores(apiFilter),
			statisticsAPIService.getSubjectCombinations(apiFilter),
			statisticsAPIService.getSubjectScores(apiFilter),
		])
			.then(([scores, combinations, subjectScores]) => {
				if (cancelled) return;
				setScoreStats(scores);
				setCombinationStats(combinations);
				setSubjectScoreStats(subjectScores);
				setError(null);
			})
			.catch((err) => {
				if (cancelled) return;
				console.error("Error fetching hoc ba tab statistics:", err);
				setError(err instanceof Error ? err.message : "Không tải được số liệu điểm học bạ tổ hợp");
			});

		return () => {
			cancelled = true;
		};
		// `refreshToken`: nút Tải lại của trang phải làm mới cả tab này.
	}, [apiFilter, refreshToken]);

	return (
		<TabsContent value="hoc-ba">
			<div ref={containerRef} className="space-y-4 md:space-y-6">
				<div className="flex items-center justify-end">
					<Button
						onClick={() => downloadChartPng(containerRef, "thong-ke-diem-hoc-ba")}
						variant="outline"
						size="sm"
						className="flex items-center gap-2 w-fit"
					>
						<Download className="h-4 w-4" />
						<span className="hidden sm:inline">Tải PNG</span>
						<span className="sm:hidden">PNG</span>
					</Button>
				</div>

				{onFiltersChange && (
					<StatisticsFilterControls
						filterType="total"
						onFilterChange={() => {}}
						chartLimit={10}
						onChartLimitChange={() => {}}
						showChartLimit={false}
						showFilterType={false}
						filters={filters}
						onFiltersChange={onFiltersChange}
						showFilters
						showReceptionDateRange={false}
					/>
				)}

				{error && (
					<p className="text-sm text-destructive" role="alert">
						{error}
					</p>
				)}

				<HocBaHistogramChart
					bins={scoreStats?.histogram}
					overallAverage={scoreStats?.overallAverage}
					counted={scoreStats?.counted}
					skipped={scoreStats?.skipped}
				/>

				<HocBaMajorChart rows={scoreStats?.byMajor} />

				<SubjectPopularityChart rows={subjectScoreStats?.rows} />

				<SubjectYearTrendChart
					rows={subjectScoreStats?.rows}
					combinations={combinationStats?.rows}
				/>

				<SubjectCombinationMatrix stats={combinationStats} />
			</div>
		</TabsContent>
	);
}
